<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PageHeader from '../components/PageHeader.vue';
import { formatDateTime, getApiErrorMessage, toIsoTimestamp } from '../utils';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const TECH_SUPPORT_TEAM_ID = 8;
const TECH_SUPPORT_TEAM_NAME = 'тех. поддержка';

const policies = ref([]);
const modelProfiles = ref([]);
const isLoadingConfig = ref(false);
const isPreviewing = ref(false);
const isLaunching = ref(false);
const previewCount = ref(null);
const previewError = ref('');
let previewTimer;

const form = ref({
  review_policy_version_id: '',
  model_profile_id: '',
  assignee_ids: [],
  team_ids: [],
  activity_from: '',
  activity_to: '',
  conversation_started_from: '',
  conversation_started_to: '',
  beforeTechSupportTransfer: false,
});

const techSupportTeam = computed(
  () =>
    teams.value.find(team => Number(team.id) === TECH_SUPPORT_TEAM_ID) ||
    teams.value.find(
      team =>
        team.name?.trim().toLowerCase() === TECH_SUPPORT_TEAM_NAME.toLowerCase()
    )
);

const publishedPolicyOptions = computed(() =>
  policies.value
    .filter(policy => policy.active_version?.state === 'published')
    .map(policy => ({
      id: policy.active_version.id,
      label: `${policy.name} · ${t('AI_REVIEWS.COMMON.VERSION', {
        version: policy.active_version.version_number,
      })}`,
    }))
);

const enabledModelOptions = computed(() =>
  modelProfiles.value.filter(profile => profile.enabled)
);

const activityRangeValid = computed(() => {
  if (!form.value.activity_from || !form.value.activity_to) return false;
  return new Date(form.value.activity_from) < new Date(form.value.activity_to);
});

const conversationRangeValid = computed(() => {
  const { conversation_started_from: from, conversation_started_to: to } =
    form.value;
  if (!from && !to) return true;
  return Boolean(from && to && new Date(from) < new Date(to));
});

const canPreview = computed(
  () =>
    Boolean(
      form.value.review_policy_version_id && form.value.model_profile_id
    ) &&
    activityRangeValid.value &&
    conversationRangeValid.value
);

const hasInvalidRange = computed(
  () =>
    (Boolean(form.value.activity_from && form.value.activity_to) &&
      !activityRangeValid.value) ||
    !conversationRangeValid.value
);

const requestAttributes = computed(() => {
  const filters = {
    assignee_ids: form.value.assignee_ids.map(Number),
    team_ids: form.value.team_ids.map(Number),
    activity_from: toIsoTimestamp(form.value.activity_from),
    activity_to: toIsoTimestamp(form.value.activity_to),
  };
  if (form.value.conversation_started_from) {
    filters.conversation_started_from = toIsoTimestamp(
      form.value.conversation_started_from
    );
    filters.conversation_started_to = toIsoTimestamp(
      form.value.conversation_started_to
    );
  }
  if (form.value.beforeTechSupportTransfer && techSupportTeam.value?.id) {
    filters.transferred_to_team_id = Number(techSupportTeam.value.id);
  }

  return {
    review_policy_version_id: Number(form.value.review_policy_version_id),
    model_profile_id: Number(form.value.model_profile_id),
    filters,
  };
});

const selectedPolicyLabel = computed(
  () =>
    publishedPolicyOptions.value.find(
      option =>
        Number(option.id) === Number(form.value.review_policy_version_id)
    )?.label || t('AI_REVIEWS.COMMON.NOT_AVAILABLE')
);

const selectedModelLabel = computed(
  () =>
    enabledModelOptions.value.find(
      profile => Number(profile.id) === Number(form.value.model_profile_id)
    )?.name || t('AI_REVIEWS.COMMON.NOT_AVAILABLE')
);

const previewBatch = async () => {
  if (!canPreview.value) return;

  try {
    isPreviewing.value = true;
    previewError.value = '';
    const { data } = await aiReviewsAPI.previewBatch(requestAttributes.value);
    previewCount.value = data.count;
  } catch (error) {
    previewCount.value = null;
    previewError.value = getApiErrorMessage(
      error,
      t('AI_REVIEWS.COMMON.API_ERROR')
    );
  } finally {
    isPreviewing.value = false;
  }
};

const schedulePreview = () => {
  window.clearTimeout(previewTimer);
  previewCount.value = null;
  previewError.value = '';
  if (!canPreview.value) return;
  previewTimer = window.setTimeout(previewBatch, 600);
};

watch(
  () => [
    form.value.review_policy_version_id,
    form.value.model_profile_id,
    form.value.assignee_ids.join(','),
    form.value.team_ids.join(','),
    form.value.activity_from,
    form.value.activity_to,
    form.value.conversation_started_from,
    form.value.conversation_started_to,
    form.value.beforeTechSupportTransfer,
  ],
  schedulePreview
);

const loadConfiguration = async () => {
  try {
    isLoadingConfig.value = true;
    const [policyResponse, modelResponse] = await Promise.all([
      aiReviewsAPI.getReviewPolicies(),
      aiReviewsAPI.getModelProfiles(),
      store.dispatch('agents/get'),
      store.dispatch('teams/get'),
    ]);
    policies.value = policyResponse.data.payload || [];
    modelProfiles.value = modelResponse.data.payload || [];
    form.value.review_policy_version_id =
      publishedPolicyOptions.value[0]?.id || '';
    form.value.model_profile_id = enabledModelOptions.value[0]?.id || '';
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoadingConfig.value = false;
  }
};

const launchBatch = async () => {
  if (previewCount.value === null || previewCount.value <= 0) return;

  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(
    t('AI_REVIEWS.NEW_BATCH.LAUNCH_CONFIRM', {
      count: previewCount.value,
      policy: selectedPolicyLabel.value,
      model: selectedModelLabel.value,
      from: formatDateTime(requestAttributes.value.filters.activity_from),
      to: formatDateTime(requestAttributes.value.filters.activity_to),
    })
  );
  if (!confirmed) return;

  try {
    isLaunching.value = true;
    const { data } = await aiReviewsAPI.createBatch(requestAttributes.value);
    useAlert(t('AI_REVIEWS.NEW_BATCH.LAUNCH_SUCCESS'));
    await router.push({
      name: 'ai_review_batch_show',
      params: { batchId: data.id },
    });
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLaunching.value = false;
  }
};

onMounted(loadConfiguration);
onBeforeUnmount(() => window.clearTimeout(previewTimer));
</script>

<template>
  <PageHeader
    :title="t('AI_REVIEWS.NEW_BATCH.TITLE')"
    :description="t('AI_REVIEWS.NEW_BATCH.DESCRIPTION')"
  />

  <div v-if="isLoadingConfig" class="p-10 text-center text-n-slate-11">
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <form v-else class="grid gap-6" @submit.prevent="launchBatch">
    <section
      class="grid gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2 md:grid-cols-2"
    >
      <div>
        <label
          for="ai-review-policy-version"
          class="block mb-1 text-sm font-medium text-n-slate-12"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.POLICY_VERSION') }}
        </label>
        <select
          id="ai-review-policy-version"
          v-model="form.review_policy_version_id"
          class="w-full h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option value="" disabled>
            {{ t('AI_REVIEWS.COMMON.REQUIRED') }}
          </option>
          <option
            v-for="option in publishedPolicyOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>
        <p
          v-if="!publishedPolicyOptions.length"
          class="mt-2 mb-0 text-xs text-n-ruby-11"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.NO_POLICIES') }}
        </p>
      </div>

      <div>
        <label
          for="ai-review-model-profile"
          class="block mb-1 text-sm font-medium text-n-slate-12"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.MODEL_PROFILE') }}
        </label>
        <select
          id="ai-review-model-profile"
          v-model="form.model_profile_id"
          class="w-full h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option value="" disabled>
            {{ t('AI_REVIEWS.COMMON.REQUIRED') }}
          </option>
          <option
            v-for="profile in enabledModelOptions"
            :key="profile.id"
            :value="profile.id"
          >
            {{ profile.name }} · {{ profile.model_identifier }}
          </option>
        </select>
        <p
          v-if="!enabledModelOptions.length"
          class="mt-2 mb-0 text-xs text-n-ruby-11"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.NO_MODELS') }}
        </p>
      </div>
    </section>

    <section
      class="grid gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2 md:grid-cols-2"
    >
      <div>
        <label
          for="ai-review-assignees"
          class="block mb-1 text-sm font-medium text-n-slate-12"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.ASSIGNEES') }}
        </label>
        <select
          id="ai-review-assignees"
          v-model="form.assignee_ids"
          multiple
          class="w-full min-h-32 p-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.available_name || agent.name || agent.email }}
          </option>
        </select>
        <p class="mt-2 mb-0 text-xs text-n-slate-11">
          {{ t('AI_REVIEWS.NEW_BATCH.MULTI_SELECT_HELP') }}
        </p>
      </div>
      <div>
        <label
          for="ai-review-teams"
          class="block mb-1 text-sm font-medium text-n-slate-12"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.TEAMS') }}
        </label>
        <select
          id="ai-review-teams"
          v-model="form.team_ids"
          multiple
          class="w-full min-h-32 p-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <p class="mt-2 mb-0 text-xs text-n-slate-11">
          {{ t('AI_REVIEWS.NEW_BATCH.MULTI_SELECT_HELP') }}
        </p>
      </div>
      <label
        v-if="techSupportTeam"
        for="ai-review-before-tech-support-transfer"
        class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak md:col-span-2"
      >
        <input
          id="ai-review-before-tech-support-transfer"
          v-model="form.beforeTechSupportTransfer"
          type="checkbox"
          class="mt-0.5 rounded border-n-strong text-n-brand focus:ring-n-brand"
        />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{
              t('AI_REVIEWS.NEW_BATCH.BEFORE_TEAM_TRANSFER', {
                team: techSupportTeam.name,
              })
            }}
          </span>
          <span class="block mt-1 text-xs text-n-slate-11">
            {{ t('AI_REVIEWS.NEW_BATCH.BEFORE_TEAM_TRANSFER_HELP') }}
          </span>
        </span>
      </label>
    </section>

    <section
      class="grid gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2 md:grid-cols-2"
    >
      <Input
        id="ai-review-activity-from"
        v-model="form.activity_from"
        type="datetime-local"
        :label="t('AI_REVIEWS.NEW_BATCH.ACTIVITY_FROM')"
      />
      <Input
        id="ai-review-activity-to"
        v-model="form.activity_to"
        type="datetime-local"
        :label="t('AI_REVIEWS.NEW_BATCH.ACTIVITY_TO')"
      />
      <Input
        id="ai-review-conversation-from"
        v-model="form.conversation_started_from"
        type="datetime-local"
        :label="t('AI_REVIEWS.NEW_BATCH.CONVERSATION_FROM')"
        :message="t('AI_REVIEWS.NEW_BATCH.OPTIONAL_RANGE')"
      />
      <Input
        id="ai-review-conversation-to"
        v-model="form.conversation_started_to"
        type="datetime-local"
        :label="t('AI_REVIEWS.NEW_BATCH.CONVERSATION_TO')"
        :message="t('AI_REVIEWS.NEW_BATCH.OPTIONAL_RANGE')"
      />
      <p
        class="p-3 mb-0 text-sm rounded-lg bg-n-blue-3 text-n-blue-11 md:col-span-2"
      >
        {{ t('AI_REVIEWS.NEW_BATCH.BOUNDARY_HELP') }}
      </p>
      <p
        v-if="hasInvalidRange"
        class="mb-0 text-sm text-n-ruby-11 md:col-span-2"
      >
        {{ t('AI_REVIEWS.NEW_BATCH.INVALID_RANGE') }}
      </p>
    </section>

    <section
      class="flex flex-wrap items-center justify-between gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <div>
        <h3 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.NEW_BATCH.PREVIEW') }}
        </h3>
        <p v-if="isPreviewing" class="mb-0 text-sm text-n-slate-11">
          {{ t('AI_REVIEWS.NEW_BATCH.PREVIEWING') }}
        </p>
        <p v-else-if="previewError" class="mb-0 text-sm text-n-ruby-11">
          {{ previewError }}
        </p>
        <p
          v-else-if="previewCount !== null"
          class="mb-0 text-sm text-n-slate-11"
        >
          {{ t('AI_REVIEWS.NEW_BATCH.PREVIEW_COUNT', { count: previewCount }) }}
        </p>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          {{ t('AI_REVIEWS.NEW_BATCH.PREVIEW_WAITING') }}
        </p>
      </div>
      <Button
        type="submit"
        :label="t('AI_REVIEWS.NEW_BATCH.LAUNCH')"
        icon="i-lucide-play"
        :is-loading="isLaunching"
        :disabled="
          previewCount === null ||
          previewCount <= 0 ||
          isPreviewing ||
          isLaunching
        "
      />
    </section>
  </form>
</template>
